import PropTypes from 'prop-types';
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Immutable from 'immutable';

import JawsComponent from 'components/JawsComponent';
import { selectMarginInfo, selectSelectedAccount } from 'state/user/selectors';
import jawsConnect from 'utils/jawsConnect';
import * as UserUtils from 'utils/user';
import * as CustomerUtils from 'utils/customer';
import Trend from 'components/Trend';
import formatNumber from 'utils/formatNumber';
import { getCurrency } from 'utils/currencies';

class PortfolioOverview extends JawsComponent {
  state = {
    currencyExchangeRate: {
      SEK: undefined,
    },
  };
  spec = {
    type: 'secure/portfolio',
    columns:
      'SHORT_NAME, LASTNZ_DIV, CLOSE, LAST, SECTOR, POSITION_VOLUME,' +
      'COST_BUY_PRICE',
    backend: 'probroker',
  };

  static propTypes = {
    jaws: PropTypes.object,
    items: PropTypes.object,
  };

  initialized = false;

  getCurrencyExchangeRate = async () => {
    const SEK = await getCurrency('SEK');

    this.setState(currentState => ({
      ...currentState,
      currencyExchangeRate: {
        SEK: SEK.data.withoutSupplementCharges,
      },
    }));
  };

  componentDidMount() {
    this.getCurrencyExchangeRate();
  } // stop JawsComponent.subscribe() from firing

  componentDidUpdate() {
    const { user } = this.props;
    const customer = UserUtils.getCustomerData(user);
    const selectedAccount = CustomerUtils.getSelectedAccount(customer);
    const selectedAccountId = selectedAccount.get('customerId');
    const userId = selectedAccount.get('customerNumber');

    const selectedAccountHasChanged =
      this.spec.portfolioId !== selectedAccountId;

    if (user && (!this.initialized || selectedAccountHasChanged)) {
      this.spec.portfolioId = selectedAccountId;
      this.spec.userId = userId;

      if (this.initialized) {
        this.resubscribe();
      } else {
        this.subscribe();
        this.initialized = true;
      }
    }
  }

  render() {
    const { items, marginInfo, selectedAccount, user } = this.props;
    const { currencyExchangeRate } = this.state;
    const customer = UserUtils.getCustomerData(user) || Immutable.Map();
    const isMargin = selectedAccount.get('marginAccount');

    if (isMargin) {
      return (
        <div className="PortfolioOverview margin">
          <div className="marketValue">
            <p>
              <b>Markedsverdi</b>
            </p>
            <p className="big-blue">
              {formatNumber(marginInfo.marketValue, 0, 'plain')}
            </p>
          </div>
          <div className="sumLoan">
            <p>
              <b>Benyttet kreditt</b>
            </p>
            <p className="big-blue">
              {formatNumber(Math.abs(marginInfo.sumLoan), 0, 'plain')}
            </p>
          </div>
          <div className="marginValue">
            <p>
              <b>Belåningsverdi</b>
            </p>
            <p className="big-blue">
              {formatNumber(marginInfo.marginValue, 0, 'plain')}
            </p>
          </div>
          <div className="collateral">
            <p>
              <b>Tilgjengelig sikkerhet</b>
            </p>
            <Trend value={marginInfo.collateral}>
              <p className="available-safety">
                {formatNumber(marginInfo.collateral, 0, 'plain')}
              </p>
            </Trend>
          </div>
        </div>
      );
    }

    const sums = items
      .filter(item => item.values.SECTOR !== 'UNKNOWN')
      .reduce(
        (sum, item) => {
          const {
            LAST,
            CLOSE,
            LASTNZ_DIV,
            SECTOR,
            POSITION_VOLUME = 0,
            COST_BUY_PRICE = 0,
          } = item.values;

          const sectorToCurrency = sector => {
            return {
              OSE: 'NOK',
              OAX: 'NOK',
              MERK: 'NOK',
              SSE: 'SEK',
            }[sector];
          };

          const getConversionRate = () => {
            const currency = sectorToCurrency(SECTOR);
            let conversionRate = 1;

            if (currency) {
              conversionRate = currencyExchangeRate[currency] || 1;
            }
            return conversionRate;
          };

          const last = LASTNZ_DIV || CLOSE || LAST || 0;
          const marketValue = POSITION_VOLUME * last * getConversionRate();
          const unrealized = marketValue - COST_BUY_PRICE;

          return {
            marketValue: sum.marketValue + marketValue,
            unrealized: sum.unrealized + unrealized,
            cost: sum.cost + COST_BUY_PRICE,
          };
        },
        { marketValue: 0, unrealized: 0, cost: 0 },
      );
    return (
      <div className="PortfolioOverview">
        <div className="marketValue">
          <p>
            <b>Markedsverdi</b>
          </p>
          <p className="big-blue">
            {formatNumber(sums.marketValue, 2, 'plain')}
          </p>
        </div>
        <div className="unrealized">
          <p>
            <b>Urealisert gevinst/tap</b>
          </p>
          <p className="big-blue">
            {formatNumber(sums.unrealized, 2, 'plain')}
          </p>
          <p className="numberOverview-small pos">
            ({formatNumber((sums.unrealized / sums.cost) * 100, 2)}%)
          </p>
        </div>
        <div className="disposable">
          <p>
            <b>Disponibel saldo</b>
          </p>
          <p className="big-blue">
            {formatNumber(customer.get('balance') || null, 0, 'plain')}
          </p>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  user: state.user,
  marginInfo: selectMarginInfo(state),
  selectedAccount: selectSelectedAccount(state),
});

export default compose(
  connect(mapStateToProps),
  jawsConnect,
)(PortfolioOverview);
